import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useReduxState } from 'hooks';
import api from 'api';
import { breakpoints } from 'styles';
import { EmptyIndicator } from 'components/common';
import { Dropdown } from 'components/common/dropdown';
import { Notification, NotificationsCenterControls } from '.';

import type { Notification as INotification } from 'shared-types';

const NotificationsCenter = () => {
  const { dispatch } = useDispatch();

  const { messages, theme, user } = useReduxState();

  const isMounted = useRef<boolean>(undefined);

  const [newItems, setNewItems] = useState(0);
  const [notifications, setNotifications] = useState<INotification[]>([]);
  const [numberOfNotifications, setNumberOfNotifications] = useState(0);
  const [showUnseenOnly, setShowUnseenOnly] = useState(false);

  useEffect(() => {
    isMounted.current = true;
    getUserNotifications({ unseen: showUnseenOnly });

    return () => {
      isMounted.current = false;
    };
  }, []);

  const getUserNotifications = async ({
    isPagination = false,
    offset = 0,
    unseen
  }) => {
    try {
      const toggleUnseen = unseen !== showUnseenOnly;

      const response = await api.get(
        `/admin/users/${user.id}/notifications?offset=${offset}&unseen=${
          toggleUnseen ? unseen : showUnseenOnly
        }`
      );

      if (isMounted.current) {
        setNewItems(response.data.numberOfUnreadNotifications);
        setNotifications(
          !isPagination || toggleUnseen
            ? response.data.notifications
            : [...notifications, ...response.data.notifications]
        );
        setNumberOfNotifications(response.data.numberOfNotifications);
        if (toggleUnseen) setShowUnseenOnly(unseen);
      }
    } catch (err) {
      setNotifications([]);
    }
  };

  const handleDeleteAll = async () => {
    try {
      await api.delete(`/admin/users/${user.id}/notifications`);

      const remainingNotifications = notifications.filter(
        (notification) => !notification.isRead
      );
      setNotifications(remainingNotifications);
      setNumberOfNotifications(
        (state) =>
          state - (notifications.length - remainingNotifications.length)
      );
    } catch (err) {
      await onError(err);
    }
  };

  const handleMark = async (id: INotification['id']) => {
    try {
      await api.put(`/admin/users/${user.id}/notifications/${id}`);
      setNewItems((state) => state - 1);

      const notificationIndex = notifications.findIndex(
        (notification) => notification.id === id
      );
      notifications[notificationIndex].isRead = true;

      if (showUnseenOnly) notifications.splice(notificationIndex, 1);

      setNotifications([...notifications]);
    } catch (err) {
      await onError(err);
    }
  };

  const handleMarkAll = async () => {
    try {
      await api.put(`/admin/users/${user.id}/notifications`);

      const unreadNotificationsAmount = notifications.filter(
        (notification) => !notification.isRead
      );
      setNewItems((state) => state - unreadNotificationsAmount.length);

      setNotifications(
        showUnseenOnly
          ? []
          : notifications.map((notification) => ({
              ...notification,
              isRead: true
            }))
      );
    } catch (err) {
      await onError(err);
    }
  };

  const handleScroll = async (ev: React.UIEvent<HTMLDivElement>) => {
    const target = ev.target as HTMLDivElement;
    const scrollHeight = target.scrollHeight - target.clientHeight;
    const scrollPercentage = (target.scrollTop * 100) / scrollHeight;

    if (notifications.length < numberOfNotifications && scrollPercentage > 70)
      await getUserNotifications({
        isPagination: true,
        offset: notifications.length,
        unseen: showUnseenOnly
      });
  };

  const handleShowUnseen = async () => {
    await getUserNotifications({ unseen: !showUnseenOnly });
  };

  const onError = async (err: Error) => await dispatch.handleErrorDispatch(err);

  return (
    <div className="notifications-center">
      <Dropdown
        before={
          <div>
            {newItems > 0 ? <div className="new-items">{newItems}</div> : null}
          </div>
        }
        buttonCustomBackgroundColor={theme.headerButtonBackgroundColor}
        buttonCustomBackgroundColorHover={
          theme.headerButtonBackgroundColorHover
        }
        icon="bell"
        iconStyle={{
          color: theme.headerIconColor,
          fontSize: '1.6rem',
          marginRight: 0
        }}
        buttonStyle={{
          borderRadius: 50,
          padding: '8px 0 8px',
          height: 30,
          width: 30
        }}
        label={messages.commonNotifications}
        category="transparent"
        contentStyle={{ padding: 0 }}
        styleContentButtons={false}
        tooltip={messages.commonNotifications}
        {...{ keepOpen: true }}
      >
        <div className="notifications">
          <div className="header">
            <NotificationsCenterControls
              onDeleteAllClick={handleDeleteAll}
              onMarkAllClick={handleMarkAll}
              onShowUnseenClick={handleShowUnseen}
              showUnseen={showUnseenOnly}
            />
          </div>

          <div className="items" onScroll={handleScroll}>
            {notifications.length ? (
              notifications.map((notification) => (
                <div className="item" key={notification.id}>
                  <Notification
                    notification={notification}
                    onClick={handleMark}
                  />
                </div>
              ))
            ) : (
              <EmptyIndicator icon="bell" title={messages.notificationsEmpty} />
            )}
          </div>
        </div>
      </Dropdown>

      <style jsx>{`
        .new-items {
          background-color: ${theme.dangerColor};
          border-radius: 10px;
          color: ${theme.dangerTextColor};
          font-size: 1rem;
          font-weight: 700;
          line-height: 1;
          margin-left: 10px;
          padding: 2px 5px 3px;
          position: absolute;
          top: -5px;
          right: -5px;
        }
        .notifications {
          display: flex;
          flex-direction: column;
          max-height: 370px;
          width: 340px;
        }
        .items {
          max-height: 320px;
          overflow: auto;
        }
        .item {
          padding: 5px;
        }
        .item:not(:first-child) {
          border-top: 1px solid ${theme.lightGrey};
        }
        @media (max-width: ${breakpoints.small - 1}px) {
          .notifications-center :global(.dropdown) {
            right: -60px !important;
          }
          .notifications {
            width: 300px;
          }
        }
      `}</style>
    </div>
  );
};

export default NotificationsCenter;
